import type {PlainObject, WmlFile} from '@wix/document-services-types'
import {DATA_TYPES as DATA_TYPES_MAP} from '@wix/format-converter-schemas'

// Based on https://developer.mozilla.org/en-US/docs/Web/CSS/Syntax
export type CssProperty = string
export type CssValue = string
export type CssDeclarationBlock = Record<CssProperty, CssValue>
export type CssSelector = string

export interface CssRule {
    selector: CssSelector
    declarations: CssDeclarationBlock
}

export type CssRules = CssRule[]

export type AllowedCssAtRules = 'preset' | 'media'
interface CssAtRuleBase<I extends AllowedCssAtRules, Q extends PlainObject> {
    identifier: I
    queries: Q
    rules: CssRules
}

export interface CssPresetQueries {
    id: string
}

export type CssPresetRule = CssAtRuleBase<'preset', CssPresetQueries>

export interface CssMediaQueries {
    id: string
    maxWidth?: number
    canvasSize?: number
}

export type CssMediaRule = CssAtRuleBase<'media', CssMediaQueries>

export type CssAtRule = CssPresetRule | CssMediaRule

export type CssBlock = CssAtRule | CssRule
export type CssAst = CssBlock[]
export type CssFile = WmlFile<'css'>

export interface CssAtRuleQueryInfo {
    key: string
    valueInfo?: {
        suffix?: string
    }
}

export const DATA_TYPES = Object.values(DATA_TYPES_MAP)

export const CSS_DELIMITER = '-'
export const CSS_DEFAULT_DECLARATION_PROPERTY_FORMAT_RESERVED_CHARS = {
    '-': '_',
    _: '\\_',
    '\\': '\\\\'
}

export const CSS_DECLARATION_PROPERTY_RESERVED_CHARS = [':', '{', '}']
export const CSS_DECLARATION_VALUE_RESERVED_CHARS = [';']
export const CSS_RESERVED_CHARS_PREFIX = '\\'

export const DEFAULT_VARIANT_KEY = ''
export const VARIANTS_DELIMITER = '&&'

const MEDIA_QUERY_AST_TO_CSS_MAP: Record<keyof CssMediaQueries, CssAtRuleQueryInfo> = {
    id: {key: 'id'},
    maxWidth: {key: 'max-width', valueInfo: {suffix: 'px'}},
    canvasSize: {key: 'canvas-size', valueInfo: {suffix: 'px'}}
}

const PRESET_QUERY_AST_TO_CSS_MAP: Record<keyof CssPresetQueries, CssAtRuleQueryInfo> = {
    id: {key: 'id'}
}

export const AT_RULE_QUERY_AST_TO_CSS_MAP: Record<AllowedCssAtRules, Record<string, CssAtRuleQueryInfo>> = {
    media: MEDIA_QUERY_AST_TO_CSS_MAP,
    preset: PRESET_QUERY_AST_TO_CSS_MAP
}

export const DEFAULT_BREAKPOINTS_MIN_RANGE = 320
